import { motion } from "framer-motion";
import { FileText, Sparkles, CheckCircle2 } from "lucide-react";

const FEATURES = [
  { title: "Laboratory Results", desc: "Professional Certificate of Analysis" },
  { title: "Easy-To-Read Summary", desc: "Plain English, not jargon" },
  { title: "What It Means", desc: "We explain every result" },
  { title: "Recommended Actions", desc: "Clear next steps you can take" },
];

const QUESTIONS = [
  "What was found?",
  "What does it mean?",
  "Is action required?",
  "What should I do next?",
];

export const ReportsShowcase = () => {
  return (
    <section id="reports" className="relative py-12 lg:py-16 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left: Visual Reports */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-6 relative"
          >
            <div className="absolute -inset-8 bg-gradient-to-br from-cyan-200/40 via-sky-200/30 to-transparent rounded-[3rem] blur-3xl" />

            {/* Stacked reports */}
            <div className="relative min-h-[640px]">
              {/* Back: COA */}
              <div className="absolute top-4 right-0 w-[85%] rounded-2xl bg-white border border-slate-200 shadow-2xl p-6 rotate-3 origin-top-right">
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center">
                      <FileText className="w-4 h-4 text-cyan-400" />
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-[0.2em] text-slate-400 font-bold">Certificate of Analysis</div>
                      <div className="text-xs font-semibold text-slate-900">SCWT-2024-1847</div>
                    </div>
                  </div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase">Lab Report</span>
                </div>
                <div className="space-y-1.5">
                  {[
                    ["pH", "7.2"],
                    ["Conductivity", "320 µS/cm"],
                    ["Total Hardness", "85 mg/L"],
                    ["Iron", "0.08 mg/L"],
                    ["E. coli", "Not detected"],
                    ["Coliforms", "Not detected"],
                    ["Turbidity", "0.4 NTU"],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between text-[11px] py-1 border-b border-slate-50">
                      <span className="text-slate-500">{k}</span>
                      <span className="font-semibold text-slate-900">{v}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Front: Summary */}
              <div className="absolute bottom-0 left-0 w-[88%] rounded-2xl bg-slate-900 text-white shadow-2xl p-7 -rotate-2 origin-bottom-left">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-cyan-500 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-[0.2em] text-cyan-400 font-bold">Easy-To-Read Summary</div>
                    <div className="text-sm font-semibold text-white">Your Water · Plain English</div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="p-3 rounded-xl bg-emerald-500/15 border border-emerald-400/20">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 font-bold mb-1">Summary</div>
                    <p className="text-sm text-white leading-snug">No tested parameters were flagged in this sample.</p>
                  </div>
                  <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-cyan-300 font-bold mb-1">What This Means</div>
                    <p className="text-xs text-slate-200 leading-relaxed">Low hardness — gentle on appliances. Iron well within drinking guidelines.</p>
                  </div>
                  <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-cyan-300 font-bold mb-1">Next Step</div>
                    <p className="text-xs text-slate-200 leading-relaxed">Continue normal use. Recommended retest in 12 months.</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-6"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-600">
              Every Customer Receives
            </span>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-[1.05]">
              Two reports.
              <br />
              <span className="water-text">One clear answer.</span>
            </h2>
            <p className="mt-5 text-lg text-slate-600 max-w-xl">
              We don't just hand you a spreadsheet of numbers. Every test includes a professional Certificate of Analysis AND a plain-English summary that tells you exactly what to do.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {FEATURES.map((f) => (
                <div key={f.title} className="rounded-2xl bg-slate-50 p-5 border border-slate-100 hover:border-cyan-200 hover:bg-white transition-all">
                  <CheckCircle2 className="w-5 h-5 text-cyan-500 mb-2" />
                  <div className="font-semibold text-slate-900 text-sm">{f.title}</div>
                  <div className="text-xs text-slate-500 mt-1">{f.desc}</div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-6 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 text-white">
              <div className="text-[10px] uppercase tracking-[0.2em] text-cyan-400 font-bold mb-3">Your report answers</div>
              <ul className="space-y-2">
                {QUESTIONS.map((q) => (
                  <li key={q} className="flex items-center gap-2.5 text-sm font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
                    {q}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
